/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { useCallback } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';

import { useStartAPetitionPageState } from 'src/app/pages/startAPetitionV2/pageContext';
import { isLoaded, isLoading } from 'src/app/shared/utils/async';

import { useGenerateAiDraft } from './hooks/useGenerateAiDraft';

type FormData = { recommendation: string };

type UseRecommendationsReturn = ModelHookResult<
	{
		error: Error | null;
		loading: boolean;
		recommendations: Array<{ id: string; recommendation: string; why: string }>;
		defaultRecommendationId: string;
	},
	{
		submit: (formData: FormData) => void;
		back: () => void;
	}
>;

export function useRecommendations(): UseRecommendationsReturn {
	const { data, actions } = useStartAPetitionPageState();
	const track = useTracking();

	const isRecommendationsVariantB = data.draft?.metadata?.variant === 'recommendations_variant_b';

	const { mutate, error, ...mutationState } = useGenerateAiDraft();

	const submit = useCallback(
		(formData: FormData) => {
			const { recommendation, type, position, why } = (() => {
				const aiRecommendations = data.draft?.input?.recommendations || [];
				const [index, aiRecommendation] =
					[...aiRecommendations.entries()].find(([, rec]) => rec.id === formData.recommendation) ?? [];
				if (index !== undefined && aiRecommendation)
					return { ...aiRecommendation, type: 'recommendation', position: index + 1 };

				return {
					type: 'write_your_own',
					recommendation: 'write_your_own',
					why: '',
					position: aiRecommendations.length + 1,
				};
			})();

			void track('sap_recommendations_submit', { ai_recommended_angle: { type, position, recommendation, why } });

			if (recommendation === 'write_your_own') {
				actions.send({
					type: 'submit',
					target: 'user',
					draft: { petitionDraft: null, recommendedDraft: null },
				});

				return;
			}

			if (isRecommendationsVariantB) {
				const selectedRecommendation = recommendation;
				actions.send({ type: 'submit', target: 'ai', draft: { input: { selectedRecommendation } } });
			} else {
				mutate(`${recommendation}. ${why}`);
			}
		},
		[track, isRecommendationsVariantB, data.draft, actions, mutate],
	);

	const back = useCallback(() => {
		void track('sap_recommendations_back');
		actions.send({ type: 'back' });
	}, [track, actions]);

	return {
		data: {
			error,
			loading: isLoading(mutationState) || isLoaded(mutationState),
			recommendations: data.draft?.input?.recommendations || [],
			defaultRecommendationId: data.draft?.input?.recommendations?.[0]?.id || '',
		},
		actions: { submit, back },
	};
}
